import { Info } from 'lucide-react'
import { Fragment, useMemo, type ReactNode } from 'react'
import { Link } from 'react-router'
import { cn } from '@/lib/cn'
import { FlowDiagram } from './FlowDiagram'
import { parseInline, parseMarkdown, type Block, type Inline, type ListBlock } from './markdown'

export const docsPath = (key: string, tab?: 'short' | 'long') => `/docs/${key}${tab === 'long' ? '?tab=long' : ''}`

function renderInline(nodes: Inline[]): ReactNode {
  return nodes.map((node, index) => {
    switch (node.type) {
      case 'text':
        return <Fragment key={index}>{node.text}</Fragment>
      case 'code':
        return (
          <code key={index} className="rounded-[5px] bg-hover px-1 py-px font-mono text-[0.9em] text-fg">
            {node.text}
          </code>
        )
      case 'strong':
        return (
          <strong key={index} className="font-semibold text-fg">
            {renderInline(node.children)}
          </strong>
        )
      case 'em':
        return <em key={index}>{renderInline(node.children)}</em>
      case 'link': {
        const className = 'font-medium text-accent underline-offset-2 hover:underline'
        if (node.href.startsWith('page:'))
          return (
            <Link key={index} to={docsPath(node.href.slice(5), 'long')} className={className}>
              {renderInline(node.children)}
            </Link>
          )
        return (
          <a key={index} href={node.href} target="_blank" rel="noreferrer" className={className}>
            {renderInline(node.children)}
          </a>
        )
      }
    }
  })
}

export const Rich = ({ text }: { text: string }) => <>{renderInline(parseInline(text))}</>

function List({ block, nested }: { block: ListBlock; nested?: boolean }) {
  return (
    <ol className={cn('space-y-1.5', nested ? 'mt-1.5' : 'my-3')}>
      {block.items.map((item, index) => (
        <li key={index} className="flex gap-2.5">
          {block.ordered ? (
            <span className="num w-5 shrink-0 pt-px text-right text-[0.92em] font-medium text-subtle" aria-hidden>
              {index + 1}.
            </span>
          ) : (
            <span className="mt-[0.62em] size-[5px] shrink-0 rounded-full bg-subtle" aria-hidden />
          )}
          <div className="min-w-0 flex-1">
            <Rich text={item.text} />
            {item.children && <List block={item.children} nested />}
          </div>
        </li>
      ))}
    </ol>
  )
}

function BlockView({ block }: { block: Block }) {
  switch (block.type) {
    case 'heading':
      return block.level === 2 ? (
        <h2 id={block.id} className="mb-2 mt-9 scroll-mt-24 text-[length:calc(17px*var(--text-scale))] font-semibold tracking-[-0.01em] text-fg first:mt-0">
          <Rich text={block.text} />
        </h2>
      ) : (
        <h3 id={block.id} className="mb-1.5 mt-5 scroll-mt-24 text-[length:calc(14.5px*var(--text-scale))] font-semibold text-fg">
          <Rich text={block.text} />
        </h3>
      )
    case 'paragraph':
      return (
        <p className="my-3">
          <Rich text={block.text} />
        </p>
      )
    case 'list':
      return <List block={block} />
    case 'note':
      return (
        <aside className="my-4 flex gap-2.5 rounded-[calc(12px*var(--radius-scale))] bg-accent-soft/70 px-4 py-3 text-fg">
          <Info className="mt-[0.2em] size-4 shrink-0 text-accent" aria-hidden />
          <div className="min-w-0 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            {block.blocks.map((child, index) => (
              <BlockView key={index} block={child} />
            ))}
          </div>
        </aside>
      )
    case 'table':
      return (
        <div className="my-4 overflow-x-auto rounded-[calc(12px*var(--radius-scale))] border border-line">
          <table className="w-full border-collapse text-left text-[0.94em]">
            <thead className="bg-table-head text-table-head-fg">
              <tr>
                {block.head.map((cell, index) => (
                  <th key={index} className="px-3 py-2 font-semibold" style={{ textAlign: block.align[index] }}>
                    <Rich text={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-t border-line align-top">
                  {block.head.map((_, index) => (
                    <td key={index} className="px-3 py-2" style={{ textAlign: block.align[index] }}>
                      <Rich text={row[index] ?? ''} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'flow':
      return <FlowDiagram edges={block.edges} />
    case 'code':
      return <pre className="my-4 overflow-x-auto rounded-[calc(12px*var(--radius-scale))] bg-hover px-4 py-3 font-mono text-[0.88em]">{block.text}</pre>
  }
}

export function useBlocks(source: string | undefined) {
  return useMemo(() => {
    if (!source) return []
    try {
      return parseMarkdown(source)
    } catch {
      return [{ type: 'paragraph', text: source } as Block]
    }
  }, [source])
}

export function DocContent({ blocks, className }: { blocks: Block[]; className?: string }) {
  return (
    <div className={cn('text-[length:calc(14.5px*var(--text-scale))] leading-[1.65] text-fg-2', className)}>
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} />
      ))}
    </div>
  )
}
